import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  Clock, CheckCircle2, XCircle, Search, Loader2, Download, Plus, X, Save,
  Settings, Zap, RefreshCw, Calculator,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OTApproval = () => {
  const [tab, setTab] = useState('records');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [records, setRecords] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [calculating, setCalculating] = useState(false);

  // Policy form
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyForm, setPolicyForm] = useState({
    policy_name: '', policy_code: '', applicable_groups: [], ot_eligibility: 'all',
    weekday_ot_rate: 1.5, holiday_ot_rate: 2.0, weekly_off_ot_rate: 2.0, night_shift_ot_rate: 2.0,
    threshold_minutes: 30, daily_cap_hours: 4, monthly_cap_hours: 60,
    rounding_rule: 'round_30', auto_detect_from_punch: true, requires_approval: true,
    slab_based: false, slabs: [], effective_from: '',
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { company_id: selectedCompany, period };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [recRes, polRes] = await Promise.all([
        axios.get(`${API_URL}/attendance/ot-records`, { params, withCredentials: true }),
        axios.get(`${API_URL}/attendance/ot-policies?company_id=${selectedCompany}`, { withCredentials: true }),
      ]);
      setRecords(recRes.data || []);
      setPolicies(polRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period, selectedCompany, statusFilter]);

  useEffect(() => { if (selectedCompany) fetchAll(); }, [selectedCompany, period, statusFilter, fetchAll]);



  const handleApprove = async (id, approved, approvedHours) => {
    try {
      await axios.post(`${API_URL}/attendance/ot-records/${id}/approve`, { approved, approved_hours: approvedHours }, { withCredentials: true });
      toast.success(`OT ${approved ? 'approved' : 'rejected'}`);
      fetchAll();
    } catch (e) { toast.error('Action failed'); }
  };

  const handleCalculateOT = async () => {
    setCalculating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/attendance/ot-records/calculate?company_id=${selectedCompany}&period=${period}`,
        {}, { withCredentials: true }
      );
      toast.success(`${data.records_created} OT records calculated`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Calculation failed'); }
    finally { setCalculating(false); }
  };

  const handleCreatePolicy = async () => {
    if (!policyForm.policy_name || !policyForm.policy_code) { toast.error('Name and code required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/attendance/ot-policies`, { ...policyForm, company_id: selectedCompany }, { withCredentials: true });
      toast.success('OT Policy created');
      setShowPolicyModal(false);
      setPolicyForm({ policy_name: '', policy_code: '', applicable_groups: [], ot_eligibility: 'all',
        weekday_ot_rate: 1.5, holiday_ot_rate: 2.0, weekly_off_ot_rate: 2.0, night_shift_ot_rate: 2.0,
        threshold_minutes: 30, daily_cap_hours: 4, monthly_cap_hours: 60,
        rounding_rule: 'round_30', auto_detect_from_punch: true, requires_approval: true,
        slab_based: false, slabs: [], effective_from: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Delete this OT policy?')) return;
    try {
      await axios.delete(`${API_URL}/attendance/ot-policies/${id}`, { withCredentials: true });
      toast.success('Policy deleted');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const filtered = records.filter(r =>
    !search || r.employee_name?.toLowerCase().includes(search.toLowerCase()) || r.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = records.filter(r => r.status === 'pending').length;
  const approvedCount = records.filter(r => r.status === 'approved').length;
  const totalOTHours = records.reduce((s, r) => s + (r.ot_hours || 0), 0);

  const otTypeBadge = (type) => {
    const map = { weekday: 'bg-blue-50 text-blue-700', holiday: 'bg-green-50 text-green-700', weekly_off: 'bg-amber-50 text-amber-700', night: 'bg-purple-50 text-purple-700' };
    return map[type] || 'bg-slate-50 text-slate-600';
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="ot-approval-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Clock className="inline-block w-8 h-8 mr-2 mb-1 text-purple-600" /> Overtime Management
            </h1>
            <p className="mt-1 text-sm text-slate-600">OT policies, records, approvals, and payroll reconciliation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="ot-calculate-btn" onClick={handleCalculateOT} disabled={calculating}>
              {calculating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" />}
              Calculate OT
            </Button>
            <Button variant="outline" size="sm" data-testid="ot-export-btn" onClick={() => {
              if (filtered.length === 0) { toast.error('No data'); return; }
              downloadCSV(filtered.map(r => ({
                Employee: r.employee_name, Code: r.employee_code, Date: r.ot_date,
                Type: r.ot_type, Scheduled: r.scheduled_hours, Actual: r.actual_hours,
                OT_Hours: r.ot_hours, Rate: r.ot_rate, Status: r.status,
              })), `ot_records_${period}.csv`);
              toast.success('Exported');
            }}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {[{ key: 'records', label: 'OT Records & Approval' }, { key: 'policies', label: 'OT Policy Master' }].map(t => (
            <button key={t.key} data-testid={`ot-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Pending</p>
            <p className="text-2xl font-semibold text-purple-600 mt-1 font-mono" data-testid="ot-pending-count">{pendingCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Approved</p>
            <p className="text-2xl font-semibold text-green-600 mt-1 font-mono">{approvedCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total OT Hours</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">{totalOTHours.toFixed(1)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Policies Active</p>
            <p className="text-2xl font-semibold text-blue-600 mt-1 font-mono">{policies.filter(p => p.is_active).length}</p>
          </div>
        </div>

        {tab === 'records' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="ot-company-select"
                className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm">
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input type="month" value={period} onChange={e => setPeriod(e.target.value)} data-testid="ot-period-select"
                className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="ot-status-filter"
                className="h-9 px-3 bg-white border border-slate-300 rounded-md text-sm">
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input data-testid="ot-search" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search employee..." className="w-full h-9 pl-9 pr-3 bg-white border border-slate-300 rounded-md text-sm" />
              </div>
            </div>

            {/* OT Records Table */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ot-records-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Sched</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actual</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">OT Hrs</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Rate</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-slate-500">
                      <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>No OT records found</p>
                      <p className="text-xs mt-1">Click "Calculate OT" to auto-detect from attendance data</p>
                    </td></tr>
                  ) : filtered.map((r, i) => (
                    <tr key={r.id || i} data-testid={`ot-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{r.employee_name || 'N/A'}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{r.employee_code}</p>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{r.ot_date}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${otTypeBadge(r.ot_type)}`}>{r.ot_type?.replace('_', ' ')}</span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono">{r.scheduled_hours}</td>
                      <td className="py-3 px-4 text-right font-mono">{r.actual_hours}</td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-purple-700">{r.ot_hours}</td>
                      <td className="py-3 px-4 text-right font-mono">{r.ot_rate}x</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          r.status === 'approved' ? 'bg-green-100 text-green-700' :
                          r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>{r.status}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {r.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <button data-testid={`approve-ot-${r.id}`} onClick={() => handleApprove(r.id, true, r.ot_hours)}
                              className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium" title="Approve">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                            <button data-testid={`reject-ot-${r.id}`} onClick={() => handleApprove(r.id, false)}
                              className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium" title="Reject">
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'policies' && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Settings className="w-4 h-4" /> OT Policy Master
              </h2>
              <Button data-testid="add-ot-policy-btn" onClick={() => setShowPolicyModal(true)} className="bg-[#0052FF] hover:bg-[#0043D1] text-white" size="sm">
                <Plus className="w-4 h-4 mr-1" /> New Policy
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {policies.length === 0 ? (
                <div className="col-span-2 bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
                  <Settings className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No OT policies defined</p>
                  <p className="text-xs text-slate-400 mt-1">Create a policy to define OT rates, thresholds, caps, and eligibility</p>
                </div>
              ) : policies.map(p => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm" data-testid={`ot-policy-${p.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">{p.policy_name}</h3>
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{p.policy_code}</span>
                    </div>
                    <div className="flex gap-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button onClick={() => handleDeletePolicy(p.id)} className="text-slate-400 hover:text-red-500 p-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center mb-3">
                    <div className="bg-blue-50 rounded p-2"><p className="text-[10px] text-blue-600">Weekday</p><p className="text-sm font-bold text-blue-700">{p.weekday_ot_rate}x</p></div>
                    <div className="bg-green-50 rounded p-2"><p className="text-[10px] text-green-600">Holiday</p><p className="text-sm font-bold text-green-700">{p.holiday_ot_rate}x</p></div>
                    <div className="bg-amber-50 rounded p-2"><p className="text-[10px] text-amber-600">Wkly Off</p><p className="text-sm font-bold text-amber-700">{p.weekly_off_ot_rate}x</p></div>
                    <div className="bg-purple-50 rounded p-2"><p className="text-[10px] text-purple-600">Night</p><p className="text-sm font-bold text-purple-700">{p.night_shift_ot_rate}x</p></div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[10px]">
                    <span className="bg-slate-50 px-2 py-0.5 rounded border border-slate-200">Threshold: {p.threshold_minutes}min</span>
                    <span className="bg-slate-50 px-2 py-0.5 rounded border border-slate-200">Daily Cap: {p.daily_cap_hours}h</span>
                    <span className="bg-slate-50 px-2 py-0.5 rounded border border-slate-200">Monthly Cap: {p.monthly_cap_hours}h</span>
                    <span className="bg-slate-50 px-2 py-0.5 rounded border border-slate-200">Rounding: {p.rounding_rule}</span>
                    {p.slab_based && <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded border border-purple-200">Slab-based</span>}
                    {p.requires_approval && <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-200">Approval Req</span>}
                  </div>
                  {p.effective_from && <p className="text-[10px] text-slate-400 mt-2">Effective from: {p.effective_from}</p>}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Create Policy Modal */}
        {showPolicyModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ot-policy-modal">
            <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-slate-50 flex items-center justify-between p-5 border-b rounded-t-xl z-10">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Create OT Policy</h2>
                  <p className="text-xs text-slate-500">Define rates, thresholds, caps, and eligibility rules</p>
                </div>
                <button onClick={() => setShowPolicyModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Policy Name *</label>
                    <input data-testid="policy-name" value={policyForm.policy_name} onChange={e => setPolicyForm({...policyForm, policy_name: e.target.value})}
                      placeholder="Workers OT Policy" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Policy Code *</label>
                    <input data-testid="policy-code" value={policyForm.policy_code} onChange={e => setPolicyForm({...policyForm, policy_code: e.target.value.toUpperCase()})}
                      placeholder="WRK-OT" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" /></div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Eligibility</label>
                    <select data-testid="policy-eligibility" value={policyForm.ot_eligibility} onChange={e => setPolicyForm({...policyForm, ot_eligibility: e.target.value})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                      <option value="all">All Employees</option><option value="group_wise">Group-wise</option><option value="department_wise">Dept-wise</option>
                    </select></div>
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Effective From</label>
                    <input data-testid="policy-effective" type="date" value={policyForm.effective_from} onChange={e => setPolicyForm({...policyForm, effective_from: e.target.value})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-slate-700 mb-2">OT Rate Multipliers</h4>
                  <div className="grid grid-cols-4 gap-2">
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Weekday</label>
                      <input data-testid="rate-weekday" type="number" step="0.1" value={policyForm.weekday_ot_rate}
                        onChange={e => setPolicyForm({...policyForm, weekday_ot_rate: parseFloat(e.target.value)})}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-center" /></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Holiday</label>
                      <input data-testid="rate-holiday" type="number" step="0.1" value={policyForm.holiday_ot_rate}
                        onChange={e => setPolicyForm({...policyForm, holiday_ot_rate: parseFloat(e.target.value)})}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-center" /></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Weekly Off</label>
                      <input data-testid="rate-weekly-off" type="number" step="0.1" value={policyForm.weekly_off_ot_rate}
                        onChange={e => setPolicyForm({...policyForm, weekly_off_ot_rate: parseFloat(e.target.value)})}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-center" /></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Night Shift</label>
                      <input data-testid="rate-night" type="number" step="0.1" value={policyForm.night_shift_ot_rate}
                        onChange={e => setPolicyForm({...policyForm, night_shift_ot_rate: parseFloat(e.target.value)})}
                        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm text-center" /></div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Min Threshold (min)</label>
                    <input data-testid="policy-threshold" type="number" value={policyForm.threshold_minutes}
                      onChange={e => setPolicyForm({...policyForm, threshold_minutes: parseInt(e.target.value)})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Daily Cap (hrs)</label>
                    <input data-testid="policy-daily-cap" type="number" step="0.5" value={policyForm.daily_cap_hours}
                      onChange={e => setPolicyForm({...policyForm, daily_cap_hours: parseFloat(e.target.value)})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Monthly Cap (hrs)</label>
                    <input data-testid="policy-monthly-cap" type="number" value={policyForm.monthly_cap_hours}
                      onChange={e => setPolicyForm({...policyForm, monthly_cap_hours: parseFloat(e.target.value)})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" /></div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-semibold text-slate-700 mb-1">Rounding</label>
                    <select data-testid="policy-rounding" value={policyForm.rounding_rule} onChange={e => setPolicyForm({...policyForm, rounding_rule: e.target.value})}
                      className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm">
                      <option value="none">No Rounding</option><option value="round_15">Round to 15 min</option>
                      <option value="round_30">Round to 30 min</option><option value="round_60">Round to 60 min</option>
                    </select></div>
                  <div className="flex flex-col justify-end gap-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid="policy-approval" checked={policyForm.requires_approval}
                        onChange={e => setPolicyForm({...policyForm, requires_approval: e.target.checked})} className="rounded border-slate-300" />
                      Requires Approval
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" data-testid="policy-slab" checked={policyForm.slab_based}
                        onChange={e => setPolicyForm({...policyForm, slab_based: e.target.checked})} className="rounded border-slate-300" />
                      Slab-based OT
                    </label>
                  </div>
                </div>

                {policyForm.slab_based && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-purple-700">OT Slabs</h4>
                      <button onClick={() => setPolicyForm({...policyForm, slabs: [...policyForm.slabs, { from_hours: 0, to_hours: 2, rate: 1.5 }]})}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium">+ Add Slab</button>
                    </div>
                    {policyForm.slabs.map((slab, i) => (
                      <div key={i} className="flex gap-2 items-center mb-1">
                        <input type="number" step="0.5" value={slab.from_hours} placeholder="From" className="w-20 border border-purple-200 rounded px-2 py-1 text-xs"
                          onChange={e => { const s = [...policyForm.slabs]; s[i].from_hours = parseFloat(e.target.value); setPolicyForm({...policyForm, slabs: s}); }} />
                        <span className="text-xs text-slate-500">to</span>
                        <input type="number" step="0.5" value={slab.to_hours} placeholder="To" className="w-20 border border-purple-200 rounded px-2 py-1 text-xs"
                          onChange={e => { const s = [...policyForm.slabs]; s[i].to_hours = parseFloat(e.target.value); setPolicyForm({...policyForm, slabs: s}); }} />
                        <span className="text-xs text-slate-500">hrs @</span>
                        <input type="number" step="0.1" value={slab.rate} className="w-16 border border-purple-200 rounded px-2 py-1 text-xs"
                          onChange={e => { const s = [...policyForm.slabs]; s[i].rate = parseFloat(e.target.value); setPolicyForm({...policyForm, slabs: s}); }} />
                        <span className="text-xs text-slate-500">x</span>
                        <button onClick={() => setPolicyForm({...policyForm, slabs: policyForm.slabs.filter((_,idx) => idx !== i)})}
                          className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 flex justify-end gap-2 p-5 border-t bg-slate-50 rounded-b-xl">
                <Button variant="outline" onClick={() => setShowPolicyModal(false)}>Cancel</Button>
                <Button data-testid="save-ot-policy-btn" onClick={handleCreatePolicy} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Create Policy
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default OTApproval;
